import { UserOrigin } from '@/client/users/types/origins.enum';
import { useSignOut } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { LoadingPage } from '@/pages/LoadingPage';
import { selectCurrentUser } from '@/store/features/users';
import { clearDemoMode } from '@/utils/demo-mode';
import { revokeHubspotCookies } from '@/utils/hubspot';
import { shutdown } from '@intercom/messenger-js-sdk';
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import userflow from 'userflow.js';

const LogoutPage = () => {
  const { signoutSilent, signoutRedirect, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const [redirect, setRedirect] = useState<boolean>(false);
  const { request } = useSignOut();
  const posthog = usePostHog();

  const signout = async () => {
    try {
      await request();
    } catch {
      // Sign-out API failure must not block the logout flow
    }

    revokeHubspotCookies();
    posthog.reset();
    userflow.reset();
    shutdown();
    clearDemoMode();

    const isKnownSsoUser =
      !!currentUser?.account?.meta?.isSSO &&
      currentUser.origin !== UserOrigin.MANUAL;

    const isRecoveringFromFailedSignIn = !currentUser;

    try {
      if (isKnownSsoUser || isRecoveringFromFailedSignIn) {
        sessionStorage.clear();
        await signoutRedirect();
      } else {
        if (isAuthenticated) {
          await signoutSilent();
        }

        sessionStorage.clear();
        sessionStorage.setItem('force_login', 'true');

        await new Promise((resolve) => setTimeout(resolve, 1000));

        setRedirect(true);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { oidc_operation: 'signout' },
      });
      sessionStorage.clear();
      sessionStorage.setItem('force_login', 'true');
      setRedirect(true);
    }
  };

  useEffect(() => {
    signout();
  }, [isAuthenticated]);

  if (!redirect) {
    return <LoadingPage message={t('auth.signOut.wait')} />;
  }

  const deployPath = process.env.REACT_APP_PREVIEW_DEPLOY_PATH;
  const basename = deployPath ? `/${deployPath}` : '';

  window.location.href = window.location.origin + basename;

  return null;
};

export default LogoutPage;
